import React, { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { API_URL } from '../config';
import { fireAnalyticsEvent } from '../utils/analytics';
import { NARRATION_ENABLED_KEY } from '../utils/audioConsent';

interface NarrationState {
  enabled: boolean;
  currentSection: number | null;
  status: 'idle' | 'loading' | 'playing' | 'paused';
  error: string | null;
  needsUserActivation: boolean;
  playbackRate: number;
}

interface NarrationContextValue {
  state: NarrationState;
  toggleEnabled: () => void;
  playSection: (sessionId: string, sectionId: number) => void;
  pauseAudio: () => void;
  resumeAudio: () => void;
  replaySection: () => void;
  activateAudio: () => void;
  setPlaybackRate: (rate: number) => void;
}

const NarrationContext = createContext<NarrationContextValue | undefined>(undefined);

const NARRATION_SPEED_KEY = 'percy_narration_speed';

export function NarrationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<NarrationState>(() => {
    const savedEnabled = localStorage.getItem(NARRATION_ENABLED_KEY);
    const savedSpeed = localStorage.getItem(NARRATION_SPEED_KEY);
    return {
      enabled: savedEnabled === 'true',
      currentSection: null,
      status: 'idle',
      error: null,
      needsUserActivation: false,
      playbackRate: savedSpeed !== null ? parseFloat(savedSpeed) : 1.0,
    };
  });

  useEffect(() => {
    const handler = () => {
      const saved = localStorage.getItem(NARRATION_ENABLED_KEY);
      setState(prev => ({ ...prev, enabled: saved === 'true' }));
    };
    window.addEventListener('percy-audio-consent', handler);
    return () => window.removeEventListener('percy-audio-consent', handler);
  }, []);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentSessionIdRef = useRef<string | null>(null);
  const narrationStateFiredRef = useRef(false);

  useEffect(() => {
    const audio = new Audio();
    // Without crossOrigin set, Firefox treats the cross-origin MP3 as a no-cors
    // response and blocks it via OpaqueResponseBlocking. Opting into CORS lets
    // the backend's Access-Control-Allow-Origin header satisfy the browser.
    audio.crossOrigin = 'anonymous';
    audioRef.current = audio;

    audio.addEventListener('play', () => {
      setState(prev => ({ ...prev, status: 'playing' }));
    });

    audio.addEventListener('pause', () => {
      setState(prev => ({ ...prev, status: prev.status === 'playing' ? 'paused' : prev.status }));
    });

    audio.addEventListener('ended', () => {
      setState(prev => ({ ...prev, status: 'idle', currentSection: null }));
    });

    audio.addEventListener('error', (e) => {
      console.error('Audio playback error:', e);
      setState(prev => ({ ...prev, status: 'idle', error: 'Failed to load audio' }));
    });

    audio.addEventListener('canplay', () => {
      // Apply saved playback rate when audio is ready
      const savedSpeed = localStorage.getItem(NARRATION_SPEED_KEY);
      audio.playbackRate = savedSpeed !== null ? parseFloat(savedSpeed) : 1.0;

      setState(prev => {
        if (prev.enabled && prev.status === 'loading') {
          audio.play().catch(err => {
            console.error('Auto-play failed:', err);
            fireAnalyticsEvent('narration_autoplay_blocked');
            setState(prevState => ({
              ...prevState,
              needsUserActivation: true,
              status: 'paused'
            }));
          });
        }
        return prev;
      });
    });

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, []);

  const toggleEnabled = useCallback(() => {
    setState(prev => {
      const newEnabled = !prev.enabled;
      localStorage.setItem(NARRATION_ENABLED_KEY, String(newEnabled));

      if (!newEnabled && audioRef.current) {
        audioRef.current.pause();
      }

      fireAnalyticsEvent('narration_toggled', { enabled: newEnabled });
      fireAnalyticsEvent('audio_toggled', { channel: 'narration', new_state: newEnabled, source: 'corner_toggle' });

      return { ...prev, enabled: newEnabled, status: newEnabled ? prev.status : 'idle' };
    });
  }, []);

  const playSection = useCallback((sessionId: string, sectionId: number) => {
    if (!audioRef.current) return;

    if (!narrationStateFiredRef.current) {
      narrationStateFiredRef.current = true;
      const saved = localStorage.getItem(NARRATION_ENABLED_KEY);
      fireAnalyticsEvent('narration_state', { enabled: saved === 'true' });
    }

    setState(prev => {
      if (!prev.enabled) return prev;

      if (prev.currentSection === sectionId && currentSessionIdRef.current === sessionId) {
        return prev;
      }

      const audio = audioRef.current!;
      audio.pause();

      currentSessionIdRef.current = sessionId;
      audio.src = `${API_URL}/narration/${sessionId}/${sectionId}`;
      audio.load();

      return {
        ...prev,
        currentSection: sectionId,
        status: 'loading' as const,
        error: null,
      };
    });
  }, []);

  const pauseAudio = useCallback(() => {
    setState(prev => {
      if (audioRef.current && prev.status === 'playing') {
        audioRef.current.pause();
      }
      return prev;
    });
  }, []);

  const resumeAudio = useCallback(() => {
    setState(prev => {
      if (audioRef.current && prev.status === 'paused') {
        audioRef.current.play().catch(err => {
          console.error('Resume failed:', err);
          setState(prevState => ({ ...prevState, error: 'Resume failed' }));
        });
      }
      return prev;
    });
  }, []);

  const replaySection = useCallback(() => {
    setState(prev => {
      if (audioRef.current && prev.currentSection !== null) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(err => {
          console.error('Replay failed:', err);
          setState(prevState => ({ ...prevState, error: 'Replay failed' }));
        });
      }
      return prev;
    });
  }, []);

  const activateAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.play()
        .then(() => {
          setState(prev => ({ ...prev, needsUserActivation: false }));
        })
        .catch(err => {
          console.error('Manual activation failed:', err);
          setState(prev => ({ ...prev, error: 'Failed to start audio' }));
        });
    }
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    localStorage.setItem(NARRATION_SPEED_KEY, String(rate));
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
    setState(prev => ({ ...prev, playbackRate: rate }));
  }, []);

  const value: NarrationContextValue = {
    state,
    toggleEnabled,
    playSection,
    pauseAudio,
    resumeAudio,
    replaySection,
    activateAudio,
    setPlaybackRate,
  };

  return (
    <NarrationContext.Provider value={value}>
      {children}
    </NarrationContext.Provider>
  );
}

export function useNarration() {
  const context = useContext(NarrationContext);
  if (context === undefined) {
    throw new Error('useNarration must be used within a NarrationProvider');
  }
  return context;
}